/**
 * LandingPage — public marketing surface at `/`.
 *
 * Composition only. Each section is its own file:
 *   - LandingNav         — sticky dark top bar
 *   - LandingHero        — dark hero with vertical chips, headline, CTAs
 *   - LandingWhyChoose   — 3 cards with soft-tile icons
 *   - LandingHowItWorks  — 4 numbered steps
 *   - LandingFeaturesGrid — dark 6-card features
 *   - LandingWhoItsFor   — 4 personas
 *   - LandingPricing     — single pricing card with feature checklist
 *   - LandingInquiryForm — lead-capture form (posts to /request-quote)
 *   - LandingFooter      — dark footer
 *
 * The vertical state lives in the page so chips in the hero AND the
 * radio buttons in the form share a single source — picking one in
 * either place reflows the other.
 */

import { type ReactNode, useState }      from 'react'
import { useNavigate }                   from 'react-router'
import './landing.css'
import { LandingNav }                    from './LandingNav'
import { LandingHero }                   from './LandingHero'
import { LandingWhyChoose }              from './LandingWhyChoose'
import { LandingHowItWorks }             from './LandingHowItWorks'
import { LandingFeaturesGrid }           from './LandingFeaturesGrid'
import { LandingWhoItsFor }              from './LandingWhoItsFor'
import { LandingPricing }                from './LandingPricing'
import { LandingInquiryForm }            from './LandingInquiryForm'
import { LandingFooter }                 from './LandingFooter'
import { PATHS }                         from '@/router/paths'
import type { Vertical, InquiryType }    from '@/types/landing'

type ScrollTarget = 'features' | 'pricing' | 'inquiry-form'

export function LandingPage(): ReactNode {
  const navigate                = useNavigate()
  const [vertical, setVertical] = useState<Vertical>('dorm')
  const [initialInquiry, setInitialInquiry] = useState<InquiryType>('quote')

  const goToSignIn = (): void => { navigate(PATHS.login) }

  const scrollTo = (id: ScrollTarget): void => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const goToForm = (inquiryType: InquiryType): void => {
    setInitialInquiry(inquiryType)
    scrollTo('inquiry-form')
  }

  return (
    <div className="landing-page">
      <LandingNav
        onScrollToSection={scrollTo}
        onSignIn={goToSignIn}
        onTryFree={() => goToForm('waitlist')}
      />

      <LandingHero
        vertical={vertical}
        onVerticalChange={setVertical}
        onPrimaryCta={() => goToForm('quote')}
        onSecondaryCta={() => scrollTo('features')}
        onSignIn={goToSignIn}
      />

      <LandingWhyChoose />
      <LandingHowItWorks />
      <LandingFeaturesGrid />
      <LandingWhoItsFor />
      <LandingPricing onCta={() => goToForm('quote')} />

      <LandingInquiryForm
        vertical={vertical}
        onVerticalChange={setVertical}
        initialInquiryType={initialInquiry}
      />

      <LandingFooter />
    </div>
  )
}
